import type { FormEvent } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, LoaderCircle, LockKeyhole, RefreshCw, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { PasswordField } from "@/components/PasswordField";
import {
  resendPasswordResetOtp,
  resetPassword,
  verifyPasswordResetOtp
} from "@/lib/api";
import {
  clearPendingPasswordResetState,
  readPendingPasswordResetState,
  savePendingPasswordResetState
} from "@/lib/pending-password-reset";

const passwordPolicySchema = z
  .string()
  .min(1, "New password is required.")
  .min(8, "Password must be at least 8 characters.")
  .regex(/[a-z]/, "Password must include a lowercase letter.")
  .regex(/[A-Z]/, "Password must include an uppercase letter.")
  .regex(/\d/, "Password must include a number.")
  .regex(/[^A-Za-z0-9]/, "Password must include a special character.");

const passwordRules = [
  "At least 8 characters",
  "One uppercase letter",
  "One lowercase letter",
  "One number",
  "One special character"
] as const;

function MessageBlock(props: {
  error: string | null;
  message: string | null;
}) {
  if (props.error) {
    return (
      <div
        className="rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700"
        data-testid="reset-password-error-message"
      >
        {props.error}
      </div>
    );
  }

  if (props.message) {
    return (
      <div
        className="rounded-[22px] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-800"
        data-testid="reset-password-success-message"
      >
        {props.message}
      </div>
    );
  }

  return null;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = searchParams.get("role") === "admin" ? "admin" : "candidate";
  const tokenFromUrl = searchParams.get("token") ?? "";

  const [pendingReset, setPendingReset] = useState(() => readPendingPasswordResetState());
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [nowValue, setNowValue] = useState(() => Date.now());

  const challenge = pendingReset?.challenge ?? null;
  const activeResetToken = pendingReset?.resetToken ?? tokenFromUrl;

  useEffect(() => {
    if (challenge && !activeResetToken) {
      const interval = window.setInterval(() => setNowValue(Date.now()), 1000);
      return () => window.clearInterval(interval);
    }

    return undefined;
  }, [activeResetToken, challenge]);

  useEffect(() => {
    if (!isComplete) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      navigate(`/auth?role=${role}&mode=signin`, { replace: true });
    }, 1800);

    return () => window.clearTimeout(timeout);
  }, [isComplete, navigate, role]);

  const resendCountdown = useMemo(() => {
    if (!challenge) {
      return 0;
    }

    return Math.max(0, Math.ceil((Date.parse(challenge.resendAvailableAt) - nowValue) / 1000));
  }, [challenge, nowValue]);

  const otpCountdown = useMemo(() => {
    if (!challenge) {
      return 0;
    }

    return Math.max(0, Math.ceil((Date.parse(challenge.expiresAt) - nowValue) / 1000));
  }, [challenge, nowValue]);

  if (!challenge && !activeResetToken && !isComplete) {
    return <Navigate replace to={`/auth?role=${role}&mode=forgot`} />;
  }

  async function handleVerifyOtp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!challenge) {
      setError("Password reset session not found. Please request a new OTP.");
      return;
    }

    setIsVerifying(true);
    setError(null);
    setMessage(null);

    try {
      const response = await verifyPasswordResetOtp({
        challengeId: challenge.challengeId,
        code
      });
      const nextState = {
        challenge,
        resetToken: response.token
      };

      savePendingPasswordResetState(nextState);
      setPendingReset(nextState);
      setCode("");
      setMessage(response.message);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to verify OTP.");
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleResendOtp() {
    if (!challenge) {
      return;
    }

    setIsResending(true);
    setError(null);
    setMessage(null);

    try {
      const response = await resendPasswordResetOtp({
        challengeId: challenge.challengeId,
        role: challenge.role
      });
      const nextState = {
        challenge: response.challenge
      };

      savePendingPasswordResetState(nextState);
      setPendingReset(nextState);
      setCode("");
      setMessage(response.message);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to resend OTP.");
    } finally {
      setIsResending(false);
    }
  }

  async function handleResetPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    const passwordValidation = passwordPolicySchema.safeParse(newPassword);

    if (!passwordValidation.success) {
      setError(passwordValidation.error.issues[0]?.message ?? "Enter a valid password.");
      return;
    }

    if (!confirmPassword) {
      setError("Confirm new password is required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }

    if (!activeResetToken) {
      setError("Verify the OTP before setting a new password.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await resetPassword({
        token: activeResetToken,
        newPassword
      });
      clearPendingPasswordResetState();
      setPendingReset(null);
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage(response.message);
      setIsComplete(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to reset password.");
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isComplete) {
    return (
      <section className="mx-auto max-w-[900px] px-6 py-10 md:py-12" data-testid="reset-password-page">
        <div className="surface p-8 md:p-10" data-testid="reset-password-card">
          <p className="eyebrow">Reset Password</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-ink" data-testid="reset-password-heading">
            Password reset completed
          </h1>
          <p className="mt-4 text-sm leading-7 text-slate">
            {message ?? "Password reset successfully."} Redirecting you to the sign-in page.
          </p>

          <div className="mt-6">
            <MessageBlock error={error} message={message} />
          </div>

          <div className="mt-8 flex justify-end">
            <Link
              className="btn-primary"
              data-testid="reset-password-back-signin-link"
              to={`/auth?role=${role}&mode=signin`}
            >
              Proceed to sign in
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (!activeResetToken) {
    return (
      <section className="mx-auto max-w-[900px] px-6 py-10 md:py-12" data-testid="reset-password-page">
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="surface p-8 md:p-10"
          data-testid="reset-password-card"
          initial={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.06, duration: 0.45, ease: "easeOut" }}
        >
          <p className="eyebrow">Reset Password</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-ink" data-testid="reset-password-heading">
            Verify the OTP
          </h1>
          <p className="mt-4 text-sm leading-7 text-slate">
            Enter the six-digit OTP sent to your registered email address to continue resetting your password.
          </p>
          {challenge ? (
            <p className="mt-3 text-sm leading-7 text-slate" data-testid="reset-password-delivery-hint">
              {challenge.deliveryHint}
            </p>
          ) : null}

          <form className="mt-8 grid gap-5" data-testid="reset-password-otp-form" onSubmit={handleVerifyOtp}>
            <label className="block space-y-2.5">
              <span className="field-label">OTP</span>
              <input
                autoComplete="one-time-code"
                className="input-field text-center text-2xl font-semibold tracking-[0.45em]"
                data-testid="reset-password-otp-input"
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                required
                value={code}
              />
            </label>

            <MessageBlock error={error} message={message} />

            <div className="rounded-[24px] border border-line/70 bg-cloud/72 px-5 py-5">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-[16px] bg-white text-royal">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-ink">Keep this window open</p>
                  <p className="mt-2 text-sm leading-7 text-slate">
                    Once the OTP is verified, you will be able to set a new password immediately.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-3">
                <Link
                  className="btn-ghost justify-start px-0 py-0 text-sm text-slate hover:bg-transparent"
                  data-testid="reset-password-back-link"
                  to={`/auth?role=${role}&mode=forgot`}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Link>
                <button
                  className="btn-secondary px-4 py-2.5"
                  data-testid="reset-password-resend-button"
                  disabled={isResending || resendCountdown > 0}
                  onClick={() => void handleResendOtp()}
                  type="button"
                >
                  {isResending ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {resendCountdown > 0 ? `Resend OTP in ${resendCountdown}s` : "Resend OTP"}
                </button>
              </div>

              <button
                className="btn-primary"
                data-testid="reset-password-verify-submit"
                disabled={isVerifying || otpCountdown === 0}
                type="submit"
              >
                {isVerifying ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                Verify OTP
              </button>
            </div>
          </form>
        </motion.div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-[900px] px-6 py-10 md:py-12" data-testid="reset-password-page">
      <div className="surface p-8 md:p-10" data-testid="reset-password-card">
        <p className="eyebrow">Reset Password</p>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight text-ink" data-testid="reset-password-heading">
          Set a new password
        </h1>
        <p className="mt-4 text-sm leading-7 text-slate">
          Create a strong password to finish resetting your account credentials.
        </p>

        <form className="mt-8 grid gap-5" data-testid="reset-password-form" onSubmit={handleResetPassword}>
          <label className="block space-y-2.5">
            <span className="field-label">New password</span>
            <PasswordField
              data-testid="reset-password-new"
              minLength={8}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              toggleButtonTestId="reset-password-new-toggle"
              value={newPassword}
            />
          </label>

          <label className="block space-y-2.5">
            <span className="field-label">Confirm new password</span>
            <PasswordField
              data-testid="reset-password-confirm"
              minLength={8}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              toggleButtonTestId="reset-password-confirm-toggle"
              value={confirmPassword}
            />
            {confirmPassword.length > 0 && newPassword.length > 0 ? (
              <p className={`text-xs font-medium ${confirmPassword === newPassword ? "text-emerald-600" : "text-rose-600"}`}>
                {confirmPassword === newPassword ? "Passwords match" : "Passwords do not match"}
              </p>
            ) : null}
          </label>

          <div className="rounded-[24px] border border-line/70 bg-cloud/72 px-5 py-5">
            <p className="text-sm font-semibold text-ink">Password rules</p>
            <ul className="mt-3 grid gap-2 text-sm leading-7 text-slate">
              {passwordRules.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </div>

          <MessageBlock error={error} message={message} />

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Link
              className="btn-ghost justify-start px-0 py-0 text-sm text-slate hover:bg-transparent"
              data-testid="reset-password-back-link"
              to={`/auth?role=${role}&mode=signin`}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </Link>
            <button className="btn-primary" data-testid="reset-password-submit" disabled={isSubmitting} type="submit">
              {isSubmitting ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <LockKeyhole className="h-4 w-4" />
              )}
              Reset Password
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
